import React from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const number = value => Number(value).toLocaleString();
const modelName = model => model.replace(/-\d{4}-\d{2}-\d{2}$/, '').replace(/^gpt-/, 'GPT-');

export default function RecordedCitationHistory({ rows, currentModel, currentVersion='geo-search-v2' }) {
  const groups = new Map();
  for (const row of rows) {
    const key = JSON.stringify([row.model, row.measurementVersion]);
    if (!groups.has(key)) groups.set(key, { key, model: row.model, version: row.measurementVersion, rows: [] });
    groups.get(key).rows.push(row);
  }
  const series = [...groups.values()].sort((a, b) => {
    const first = group => group.rows.find(row => Number(row.completedRuns) > 0)?.period || '';
    return first(a).localeCompare(first(b)) || a.model.localeCompare(b.model);
  });

  if (!series.length) return <p className="py-16 text-center text-muted-foreground">No successful runs were recorded in this date range.</p>;

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Earlier results are preserved. Models are shown separately because the search method changed.
        Counts depend on how many keywords ran; gaps mean no successful runs were recorded.
      </p>
      {series.map(group => {
        const measured = group.rows.filter(row => Number(row.completedRuns) > 0);
        const totalRuns = measured.reduce((sum, row) => sum + Number(row.completedRuns), 0);
        const totalCitations = measured.reduce((sum, row) => sum + Number(row.totalCitations), 0);
        const current = group.model === currentModel && group.version === currentVersion;
        const label = group.version === 'legacy' ? 'Earlier tracking' : current ? 'Current tracking' : 'Earlier model or method';
        return (
          <section key={group.key} aria-label={`${label}: ${group.model}`} className="space-y-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold">{label} · {modelName(group.model)}</h3>
                <p className="text-xs text-muted-foreground">{number(totalRuns)} successful runs · {number(totalCitations)} citations recorded</p>
              </div>
              <p className="text-xs text-muted-foreground">{measured[0].period} to {measured[measured.length - 1].period}</p>
            </div>
            <div className="h-[240px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={group.rows} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} minTickGap={30} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--popover))', borderRadius: '6px' }}
                    labelFormatter={(label, payload) => `${label} · ${payload?.[0]?.payload?.completedRuns || 0} successful runs`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="linear" dataKey="totalCitations" name="Total citations" stroke="#475569" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 4 }} connectNulls={false} />
                  <Line type="linear" dataKey="brandCitations" name="Brand citations (owned + mentions)" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} connectNulls={false} />
                  <Line type="linear" dataKey="competitorCitations" name="Competitor citations" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} connectNulls={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
        );
      })}
      <p className="text-xs text-muted-foreground">Historical totals come from each saved run. Earlier records combine owned citations and brand mentions; their original split is unavailable.</p>
    </div>
  );
}
